/* eslint-disable camelcase */

import qs from "qs";
import axiosInstance from "../axios";
import {
  GetOrdersParams,
  HospitalForm,
  OrdersExportParams,
  PatientForm,
  SingleOrderParams,
} from "./dashboard.interface";
import { createAxiosInstance } from "../axios-client";

export const getOrders = async ({
  sort,
  filter,
  searchFilter,
  page,
  agent,
  pickup_date_from,
  pickup_date_to,
  created_date_from,
  created_date_to,
  approval_form_status,
  hospital_ids,
  perPage,
  reason,
  travel_category,
  transport_company_id,
}: GetOrdersParams) => {
  const search = searchFilter !== "" ? searchFilter : undefined;
  const dateFrom = pickup_date_from !== "" ? pickup_date_from : undefined;
  const dateTo = pickup_date_to !== "" ? pickup_date_to : undefined;
  const createdFrom = created_date_from !== "" ? created_date_from : undefined;
  const createdTo = created_date_to !== "" ? created_date_to : undefined;
  const approvalForm =
    approval_form_status !== "" ? approval_form_status : undefined;
  const hospitalIds = hospital_ids && hospital_ids.length > 0 ? hospital_ids : undefined;
  const cancelReason = reason !== "" ? reason : undefined;
  const travelCategory = travel_category !== "" ? travel_category : undefined;

  const searchParams = qs.stringify({
    sort,
    filter,
    search,
    page,
    agent,
    pickup_date_from: dateFrom,
    pickup_date_to: dateTo,
    created_date_from: createdFrom,
    created_date_to: createdTo,
    approval_form_status: approvalForm,
    hospital_ids: hospitalIds,
    perPage,
    reason: cancelReason,
    travel_category: travelCategory,
    transport_company_id: transport_company_id || undefined,
  }, {
    arrayFormat: 'brackets'
  });
  const { data } = await axiosInstance.get(
    `api/transporter/orders?${searchParams}`,
  );
  return data;
};

export const getCheckUpdate = async ({
  lastUpdate,
}: {
  lastUpdate: string | undefined;
}) => {
  const { data } = await axiosInstance.get(
    `api/transporter/orders/check-update?last_updated_order_date=${lastUpdate}`,
  );
  return data;
};
export const getOrdersExport = async ({
  sort,
  filter,
  searchFilter,
  agent,
  transport_company_id,
  language,
  format,
  settings,
}: OrdersExportParams) => {
  const search = searchFilter !== "" ? searchFilter : undefined;
  const searchParams = qs.stringify({
    sort,
    filter,
    search,
    agent,
    transport_company_id: transport_company_id || undefined,
    language,
    format,
  });

  const response = await axiosInstance.post(
    `api/transporter/orders/export?${searchParams}`,
    {
      columns_settings: [
        ...(settings ?? []),
        { id: 99, label: "table_birthday", hidden: false },
      ],
    },
    {
      responseType: "arraybuffer",
    },
  );

  const contentType = response.headers["content-type"] || "";
  
  if (contentType.includes("application/json")) {
    const textDecoder = new TextDecoder();
    const jsonText = textDecoder.decode(response.data);
    const jsonData = JSON.parse(jsonText);
    return { data: jsonData, isQueued: true, message: jsonData.message };
  }

  return { data: new Blob([response.data]), isQueued: false };
};

export const getOrdersExports = async ({
  page,
  perPage,
}: {
  page?: number;
  perPage?: number;
}) => {
  const searchParams = qs.stringify({
    page,
    perPage,
  });
  const { data } = await axiosInstance.get(
    `api/transporter/orders/export/${searchParams ? `?${searchParams}` : ""}`,
  );
  return data;
};

export const downloadOrderExport = async (downloadUrl: string) => {
  let apiPath = downloadUrl;
  try {
    const url = new URL(downloadUrl);
    apiPath = url.pathname;
    if (apiPath.startsWith("/api/")) {
      apiPath = apiPath.substring(1);
    } else if (apiPath.startsWith("/transporter/")) {
      apiPath = `api${apiPath}`;
    } else if (apiPath.startsWith("/")) {
      apiPath = `api${apiPath}`;
    }
  } catch {
    if (downloadUrl.startsWith("/api/")) {
      apiPath = downloadUrl;
    } else if (downloadUrl.startsWith("/transporter/")) {
      apiPath = `api${downloadUrl}`;
    } else if (downloadUrl.startsWith("/")) {
      apiPath = `api${downloadUrl}`;
    }
  }
  
  const { data } = await axiosInstance.get(apiPath, {
    responseType: "blob",
  });
  return data;
};

export const singleOrderExport = async ({
  id,
  language,
  format,
  settings,
}: SingleOrderParams) => {
  const searchParams = qs.stringify({
    language,
    format,
  });

  const { data } = await axiosInstance.post(
    `api/transporter/orders/export/${id}?${searchParams}`,
    {
      // Birthday should not be shown in the table but be part of the export
      columns_settings: [
        ...(settings ?? []),
        { id: 99, label: "table_birthday", hidden: false },
      ],
    },
    {
      responseType: "blob",
    },
  );
  return data;
};

export const changeStatus = async (data: any) => {
  return axiosInstance.post("api/transporter/orders/status/update", data);
};

export const dismissOrderStatus = async (orderId: string) => {
  const formData = new FormData();
  formData.append("order_id", orderId);
  return axiosInstance.post("api/transporter/orders/status/dismiss", formData);
};

export const updateOrderData = async ({
  id,
  data,
}: {
  id: string | undefined;
  data: PatientForm;
}) => {
  return axiosInstance.post(`api/transporter/orders/update/${id}`, data);
};

export const getScanImage = async (scanUrl: string) => {
  const { data } = await axiosInstance.get(scanUrl, { responseType: "blob" });
  return data;
};

export const updateHospital = async ({
  id,
  data,
}: {
  id: string | undefined;
  data: HospitalForm;
}) => {
  return axiosInstance.post(`api/transporter/orders/update/${id}`, data);
};

export const listUserFilter = async (transportCompanyId?: string) => {
  const params = qs.stringify({
    transport_company_id: transportCompanyId || undefined,
  });
  return axiosInstance.get(
    `api/transporter/orders/filters/users${params ? `?${params}` : ""}`,
  );
};

export const listTransportCompaniesFilter = async (params: Record<string, unknown>) => {
  const searchParams = qs.stringify(params, { arrayFormat: "brackets" });
  const { data } = await axiosInstance.get(
    `api/transporter/orders/filters/transport-companies${searchParams ? `?${searchParams}` : ""}`,
  );
  return data;
};

export const getProfileInfo = async () => {
  return createAxiosInstance().get("api/transporter/profiles");
};

export const updateProfileInfo = async (data: any) => {
  return axiosInstance.post("api/transporter/profiles", data);
};
export const updateProfileSettings = async (data: any) => {
  return axiosInstance.post("api/transporter/profiles/settings", data);
};

export const updateDragAndDrop = async (data: any) => {
  const dataParams = JSON.stringify(data);

  return axiosInstance.post("api/transporter/profiles/settings", {
    columns_settings: dataParams,
  });
};

export const getAllNotifications = async () => {
  return axiosInstance.get("api/transporter/notifications");
};

export const readAllNotifications = async () => {
  return axiosInstance.post("api/transporter/notifications/read-all");
};

export const currentLanguage = async (data: { lang: string }) => {
  return axiosInstance.post(`api/transporter/lang`, data);
};

export const createContactUs = async (data: any) => {
  return axiosInstance.post("api/contact-forms", data);
};

export const approveApprovalForm = async (orderId: string | undefined) => {
  return axiosInstance.post(
    `api/transporter/orders/${orderId}/approval-form/approve`,
  );
};

export const cancelOrder = async ({ id, reason }: { id: string; reason: string }) => {
  const formData = new FormData();
  formData.append("id", id);
  formData.append("reason", reason);
  return axiosInstance.post("api/transporter/orders/cancel", formData);
};

export const transferOrder = async ({
  orderId,
  transferTo,
}: {
  orderId: string;
  transferTo: string;
}) => {
  const formData = new FormData();
  formData.append("transfer_to", transferTo);
  return axiosInstance.post(`api/transporter/orders/${orderId}/transfer`, formData);
};

export const getHospitals = async (transportCompanyId?: string) => {
  const params = qs.stringify({
    transport_company_id: transportCompanyId || undefined,
  });
  return axiosInstance
    .get(`api/transporter/orders/filters/hospitals${params ? `?${params}` : ""}`)
    .then((data) => data.data);
};

export const getAppConfig = async (lang: string) => {
  const { data } = await axiosInstance.get(
    `api/transporter/appconfig?lang=${lang}`,
  );
  return data;
};

export const getArchivedOrders = async ({
  sort,
  searchFilter,
  page,
  perPage,
  hospital_ids,
  agent,
  transport_company_id,
}: {
  sort?: string;
  searchFilter?: string;
  page?: number;
  perPage?: number;
  hospital_ids?: string[];
  agent?: string;
  transport_company_id?: string;
}) => {
  const search = searchFilter !== "" ? searchFilter : undefined;
  const hospitalIds = hospital_ids && hospital_ids.length > 0 ? hospital_ids : undefined;

  const searchParams = qs.stringify({
    sort,
    search,
    page,
    perPage,
    hospital_ids: hospitalIds,
    agent,
    transport_company_id: transport_company_id || undefined,
  }, {
    arrayFormat: 'brackets'
  });
  const { data } = await axiosInstance.get(
    `api/transporter/orders/archived?${searchParams}`,
  );
  return data;
};

export type StatisticsPeriod = "all" | "today" | "week" | "month" | "year" | "custom";

export interface StatisticsParams {
  hospital_ids?: string[];
  period?: StatisticsPeriod;
  period_from?: string;
  period_to?: string;
  module?: string[];
}

export interface StatisticsResponse {
  data: {
    orders_count: {
      current: number;
      previous: number;
      change: number;
    };
    orders_per_hospital: Array<{
      hospital_id: string;
      hospital_name: string;
      count: number;
      growth_percentage?: number;
      median_time_deviation_minutes: number;
      is_typically_late: boolean;
    }>;
    orders_by_period: {
      group_by: string;
      data: Array<{
        period: string;
        count: number;
      }>;
      total: number;
    };
    orders_today_per_hour: {
      data: Array<{
        hour: string;
        count: number;
      }>;
      total: number;
    };
    orders_vs_cancellations: {
      cancelled: number;
      ordered: number;
      cancelled_percentage: number;
      ordered_percentage: number;
    };
    transport_companies_statistics?: Array<{
      transport_company_id: string;
      transport_company_name: string;
      orders_count: number;
      orders_percentage: number;
      median_time_deviation_minutes: number;
      is_typically_late: boolean;
    }>;
    capacity_by_percentage?: {
      suggested_time: { count: number; percentage: number };
      not_suggested_time: { count: number; percentage: number };
      total: number;
    };
  };
}

export const getStatistics = async ({
  hospital_ids,
  period,
  period_from,
  period_to,
  module: moduleParam,
}: StatisticsParams): Promise<StatisticsResponse> => {
  const hospitalIds = hospital_ids && hospital_ids.length > 0 ? hospital_ids : undefined;

  const params: Record<string, unknown> = {
    hospital_ids: hospitalIds,
    period,
  };
  if (period === "custom" && period_from && period_to) {
    params.period_from = period_from;
    params.period_to = period_to;
  }
  if (moduleParam && moduleParam.length > 0) {
    params.module = moduleParam;
  }

  const searchParams = qs.stringify(params, {
    arrayFormat: "brackets",
  });

  const { data } = await axiosInstance.get(
    `api/transporter/statistics${searchParams ? `?${searchParams}` : ""}`,
  );
  return data;
};